import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { SITE } from "@/content/site";
import { CTAButton } from "@/components/site/CTAButton";
import { MaskedLines, Reveal } from "@/components/motion/Reveal";

export const PlaceholderTag = ({ children = "Placeholder", className, testId }) => (
  <span
    data-testid={testId}
    className={cn(
      "inline-flex items-center rounded border border-dashed border-[#2DD4D4]/50 bg-[#0A0B0D]/60 px-1.5 py-0.5 font-mono text-xs uppercase tracking-wider text-[#2DD4D4]",
      className,
    )}
  >
    {children}
  </span>
);

export const PhoneValue = ({ testId, className }) =>
  SITE.phone ? (
    <a href={SITE.phoneHref} className={cn("hover:text-white", className)} data-testid={testId}>
      {SITE.phone}
    </a>
  ) : (
    <span className={cn("inline-flex flex-wrap items-center gap-2", className)} data-testid={testId}>
      <span>Phone number</span>
      <PlaceholderTag>Placeholder</PlaceholderTag>
    </span>
  );

export const ConceptBadge = ({ className, testId }) => (
  <span
    data-testid={testId}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-2.5 py-1 font-mono text-xs uppercase tracking-wider text-white/90",
      className,
    )}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-[#2DD4D4]" aria-hidden="true" />
    Concept mockup
  </span>
);

export const BookCallButton = ({ testId = "book-call-button", className, variant = "primary" }) => {
  if (SITE.bookCallUrl) {
    return (
      <CTAButton href={SITE.bookCallUrl} target="_blank" rel="noopener noreferrer" variant={variant} className={className} testId={testId}>
        Book a call
      </CTAButton>
    );
  }
  const onClick = () =>
    toast("Online booking is coming soon", {
      icon: <CalendarClock className="h-4 w-4" />,
      description: `For now, email ${SITE.email} or send the form and we'll set up a time.`,
    });
  return (
    <CTAButton onClick={onClick} variant={variant} className={className} testId={testId}>
      Book a call
    </CTAButton>
  );
};

export const SectionIntro = ({ eyebrow, title, copy, className, testId, children }) => (
  <div className={cn("max-w-2xl", className)}>
    {eyebrow && (
      <Reveal as="p" className="eyebrow">
        {eyebrow}
      </Reveal>
    )}
    <MaskedLines
      as="h2"
      onView
      lines={Array.isArray(title) ? title : [title]}
      className="mt-5 text-3xl font-semibold leading-[1.05] tracking-[-0.035em] sm:text-4xl lg:text-5xl"
      lineClassName="text-sheen"
      testId={testId}
    />
    {copy && (
      <Reveal as="p" delay={0.1} className="mt-6 text-base leading-relaxed text-[#A1A1AA] sm:text-lg">
        {copy}
      </Reveal>
    )}
    {children}
  </div>
);
